import { useCallback } from 'react';
import toast from 'react-hot-toast';
import { useTranslate } from '@tolgee/react';
import { useDispatch, useSelector } from 'react-redux';

import { INSTAGRAM } from '../../../../../config/constants';
import { lomavisCreatorRootState } from '../../../../../pages/lomavisCreator';
import {
  setModalUiContent,
  setPostDataAttribute
} from '../../../../../redux/slices/lomavisCreator/lomavisCreator';
import { VIDEO } from '../../../config/constants';
import { POST_TYPE_STORY_KEY } from '../../../config/platformFeatures';

const isVideoMedia = (item: any) =>
  item?.media_type === VIDEO || item?.type === VIDEO;

export const useInstagramMusicControls = (platform: string = INSTAGRAM) => {
  const { t } = useTranslate();
  const dispatch = useDispatch();

  const postData = useSelector(
    (state: lomavisCreatorRootState) => state.lomavisCreator.postData[platform]
  );
  const postTypeSelected = useSelector(
    (state: lomavisCreatorRootState) =>
      state.lomavisCreator.postTypeSelected[platform]
  );
  const instagramDirectConnect = useSelector(
    (state: lomavisCreatorRootState) =>
      state.lomavisCreator.instagram_direct_connect
  );
  const music = postData?.additional_publication_data?.instagram_music;
  const media = postData?.media || [];
  const videoCount = media.filter(isVideoMedia).length;
  const hasVideo = videoCount > 0;
  const isStoryPostType = postTypeSelected === POST_TYPE_STORY_KEY;
  const hasSingleVideo = media.length === 1 && videoCount === 1;
  const canUseInstagramMusic = !isStoryPostType && hasSingleVideo;
  const hasUnsupportedVideoSelection =
    (hasVideo || isStoryPostType) && !canUseInstagramMusic;
  const musicUnavailableReason = isStoryPostType
    ? t(
        'lomavisCreator.IG.music.notSupportedForStories',
        "Instagram music can't be added to stories."
      )
    : videoCount > 1
    ? t(
        'lomavisCreator.IG.music.multipleVideosNotSupported',
        "Instagram music can't be used with multiple videos."
      )
    : t(
        'lomavisCreator.IG.music.singleVideoRequired',
        'Instagram music can only be added to single video posts.'
      );

  const openMusicModal = useCallback(() => {
    if (instagramDirectConnect) {
      toast.error(
        t(
          'lomavisCreator.IG.music.directConnectNotSupported',
          'Instagram music is only available for accounts connected via Meta Business Suite.'
        ),
        {
          duration: 4000,
          position: 'top-right'
        }
      );
      return;
    }

    if (isStoryPostType) {
      toast.error(musicUnavailableReason, {
        duration: 4000,
        position: 'top-right'
      });
      return;
    }

    if (!hasVideo) {
      toast.error(t('lomavisCreator.IG.music.requiresVideo'), {
        duration: 4000,
        position: 'top-right'
      });
      return;
    }

    if (!canUseInstagramMusic) {
      toast.error(musicUnavailableReason, {
        duration: 4000,
        position: 'top-right'
      });
      return;
    }

    dispatch(
      setModalUiContent({
        modal: 'IGMusic',
        attribute: 'toggle',
        value: true
      })
    );
  }, [
    canUseInstagramMusic,
    dispatch,
    hasVideo,
    instagramDirectConnect,
    isStoryPostType,
    musicUnavailableReason,
    t
  ]);

  const clearMusic = useCallback(() => {
    const next = { ...(postData?.additional_publication_data || {}) };
    delete next.instagram_music;

    dispatch(
      setPostDataAttribute({
        platform,
        attribute: 'additional_publication_data',
        value: next
      })
    );
  }, [dispatch, platform, postData?.additional_publication_data]);

  return {
    music,
    hasVideo,
    canUseInstagramMusic,
    hasUnsupportedVideoSelection,
    musicUnavailableReason,
    openMusicModal,
    clearMusic
  };
};
